import React, { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Subscription } from 'rxjs'
import { EnvironmentContext } from '../../../providers/environment/EnvironmentContext'
import { environmentService } from '../../../providers/environment/EnvironmentService'
import { SessionContext } from '../../../providers/session/SessionContext'
import { isDefined } from '../../../utils/TypeCheckers'
import { cancelAbortController, RunOnceEffect, unSubscribe } from '../../../utils/Utils'
import { BackIconStyled } from '../../bespoke/sports-betting/landing-page/sliding-panel/styled'
import LoadingSpinner from '../loading-spinner/LoadingSpinner'
import { HeaderStyled, LegalHeaderWrapperStyled, LegalPolicyStyled, NotFoundStyled } from './styled'
import { LegalRequestDTO, LegalResponseDTO } from './types'

interface LegalProperties {
    code?: string
    noTitle?: boolean
    onClose?: () => void
    requireAcceptance?: boolean
    textButtonAccept?: string
    onAccept?: (hasAccepted: boolean, code: string) => void // Callback when user accepts terms
}

export const LegalPage = ({
    code,
    noTitle,
    onClose,
    requireAcceptance = false,
    onAccept,
    textButtonAccept,
}: LegalProperties): React.ReactElement => {
    const location = useLocation()
    const sessionContext = React.useContext(SessionContext)
    const environmentContext = React.useContext(EnvironmentContext)

    const [loaded, setLoaded] = useState<boolean>(false)
    const [notFound, setNotFound] = useState<boolean>(false)
    const [systemReady, setSystemReady] = useState<boolean>(false)
    const [content, setContent] = useState<string>('')
    const [title, setTitle] = useState<string>('')
    const isMobile = environmentContext.isMobileDevice()

    // New state variables for scroll tracking and acceptance
    const [hasScrolledToEnd, setHasScrolledToEnd] = useState<boolean>(!requireAcceptance)
    const [hasAccepted, setHasAccepted] = useState<boolean>(false)
    const scrollContainerRef = useRef<HTMLDivElement>(null)
    const bottomDetectorRef = useRef<HTMLDivElement>(null)

    // Intersection Observer for bottom detection (more reliable)
    useEffect(() => {
        if (!requireAcceptance || hasScrolledToEnd || !bottomDetectorRef.current) return

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setHasScrolledToEnd(true)
                        observer.disconnect()
                    }
                })
            },
            {
                root: scrollContainerRef.current,
                rootMargin: '0px',
                threshold: 0.1,
            }
        )

        observer.observe(bottomDetectorRef.current)

        return () => observer.disconnect()
    }, [requireAcceptance, hasScrolledToEnd, content])

    const [footerHeight, setFooterHeight] = useState<number>(0)
    RunOnceEffect(() => {
        let heightSubscription: Subscription = environmentService.watchFooterHeight().subscribe((height: number) => {
            setFooterHeight(height)
        })
        return () => {
            unSubscribe(heightSubscription)
        }
    })

    RunOnceEffect(() => {
        let loaderSubscription = sessionContext.watchSystemReady().subscribe((systemReady: boolean) => {
            setSystemReady(systemReady)
        })
        return () => {
            unSubscribe(loaderSubscription)
        }
    })

    useEffect(() => {
        let policyCode = code
        let abortController = new AbortController()
        if (!policyCode) {
            const paths = location.pathname.split('/')
            if (paths.length > 2) {
                policyCode = paths[2]
            }
        }
        if (policyCode === 'fe_pep') {
            setNotFound(false)
            setLoaded(true)
            setContent(PEP_CONTENT)
            setTitle('Politically Exposed Person (PEP)')
        } else if (policyCode) {
            setLoaded(false)
            sessionContext
                .post<LegalRequestDTO, LegalResponseDTO>('/legal/v1/policy', {
                    code: policyCode,
                })
                .then((response) => {
                    setNotFound(false)
                    setLoaded(true)
                    setContent(response.data.contents)
                    setTitle(response.data.title)
                })
                .catch((error) => {
                    setNotFound(true)
                    setLoaded(true)
                })
        } else {
            setNotFound(true)
            setLoaded(true)
        }
        return () => {
            cancelAbortController(abortController)
        }
    }, [location.pathname, systemReady, code, sessionContext])

    // Scroll detection for forced scroll-to-end
    const handleScroll = () => {
        if (!requireAcceptance || hasScrolledToEnd || !scrollContainerRef.current) return

        const container = scrollContainerRef.current
        const { scrollTop, scrollHeight, clientHeight } = container

        // Check if content is not scrollable (height fits in container)
        if (scrollHeight <= clientHeight) {
            setHasScrolledToEnd(true)
            return
        }

        // Check if scrolled to within 20px of the bottom (more lenient)
        const isNearBottom = scrollTop + clientHeight >= scrollHeight - 20

        if (isNearBottom) {
            setHasScrolledToEnd(true)
        }
    }

    // Fallback: Add a timer to check scroll position after component mounts
    useEffect(() => {
        if (requireAcceptance && scrollContainerRef.current) {
            const container = scrollContainerRef.current
            const { scrollHeight, clientHeight } = container

            // If content doesn't need scrolling, enable accept immediately
            if (scrollHeight <= clientHeight + 10) {
                setHasScrolledToEnd(true)
            }
        }
    }, [requireAcceptance, content])

    const onAcceptTerms = () => {
        setHasAccepted(true)
        if (onAccept) {
            onAccept(true, code || '')
        }
    }

    const onCloseClicked = () => {
        if (onClose) {
            onClose()
        }
    }

    const renderDocument = (): React.ReactElement => {
        return (
            <>
                {!noTitle && (
                    <LegalHeaderWrapperStyled width={window.innerWidth}>
                        {onClose && (
                            <BackIconStyled>
                                <i className="fas fa-chevron-left" onClick={() => onCloseClicked()} />
                            </BackIconStyled>
                        )}
                        <HeaderStyled width={window.innerWidth} hasOnClose={isDefined(onClose)}>
                            {title || ''}
                        </HeaderStyled>
                    </LegalHeaderWrapperStyled>
                )}
                <LegalPolicyStyled
                    ref={scrollContainerRef}
                    isMobile={isMobile}
                    id={'policy'}
                    noFooter={noTitle}
                    internal={false}
                    footerHeight={footerHeight}
                    onScroll={requireAcceptance ? handleScroll : undefined}
                >
                    {/* Scroll indicator for required acceptance */}
                    {requireAcceptance && !hasScrolledToEnd && (
                        <div
                            style={{
                                position: 'fixed',
                                bottom: requireAcceptance ? '120px' : '20px',
                                right: '20px',
                                backgroundColor: 'rgba(0,0,0,0.7)',
                                color: 'white',
                                padding: '10px 15px',
                                borderRadius: '20px',
                                fontSize: '12px',
                                zIndex: 1000,
                                animation: 'pulse 2s infinite',
                            }}
                        >
                            📄 Please scroll to read all terms
                        </div>
                    )}

                    {/* local rule for spacing between paragraphs in this page only */}
                    <style>{`
                        #policy p { margin-bottom: 12px; margin-top: 12px; }
                        @keyframes pulse {
                            0% { opacity: 0.7; }
                            50% { opacity: 1; }
                            100% { opacity: 0.7; }
                        }
                    `}</style>
                    <div dangerouslySetInnerHTML={{ __html: content }} />

                    {/* Bottom detector for intersection observer */}
                    <div
                        ref={bottomDetectorRef}
                        style={{
                            height: '1px',
                            width: '100%',
                            backgroundColor: 'transparent',
                        }}
                    />

                    {/* Accept button - only shown if acceptance is required and user has scrolled to end */}
                    {requireAcceptance && (
                        <div
                            style={{
                                position: 'sticky',
                                bottom: '0',
                                padding: '20px',
                                textAlign: 'center',
                                borderTop: '1px solid #ddd',
                                marginTop: '30px',
                            }}
                        >
                            <button
                                onClick={onAcceptTerms}
                                disabled={!hasScrolledToEnd || hasAccepted}
                                style={{
                                    backgroundColor: hasScrolledToEnd && !hasAccepted ? '#007bff' : '#ccc',
                                    color: hasScrolledToEnd && !hasAccepted ? 'white' : '#666',
                                    border: 'none',
                                    padding: '12px 30px',
                                    fontSize: '16px',
                                    borderRadius: '5px',
                                    cursor: hasScrolledToEnd && !hasAccepted ? 'pointer' : 'not-allowed',
                                    transition: 'all 0.3s ease',
                                }}
                            >
                                {hasAccepted
                                    ? '✅ Terms Accepted'
                                    : hasScrolledToEnd
                                    ? textButtonAccept || 'I Accept These Terms & Conditions'
                                    : 'Please read all terms first'}
                            </button>
                            <div style={{ fontSize: '12px', marginTop: '10px' }}>
                                {!hasScrolledToEnd && 'You must scroll to the end to accept the terms'}
                                {hasScrolledToEnd && !hasAccepted && 'Click the button above to accept'}
                                {hasAccepted && 'Thank you for accepting our terms and conditions'}
                            </div>
                        </div>
                    )}
                </LegalPolicyStyled>
            </>
        )
    }

    const renderNotFound = (): React.ReactElement => {
        return (
            <>
                <NotFoundStyled>Failed to find that policy</NotFoundStyled>
            </>
        )
    }

    const renderPage = (): React.ReactElement => {
        return (
            <>
                <>{notFound ? renderNotFound() : renderDocument()}</>
            </>
        )
    }

    return <>{loaded && systemReady ? renderPage() : <LoadingSpinner />}</>
}

const PEP_CONTENT = `
<h2>Politically Exposed Person (PEP)</h2>

<p>
  In accordance with Anti-Money Laundering (AML) regulations there is an
  obligation for the Company to undertake Enhanced Customer Due Diligence (EDD)
  on those clients who are classified as a Politically Exposed Person (PEP) or
  individuals with adverse media mentions. Please read the definition below
  carefully, and confirm whether you are, or are not, a PEP.
</p>

<h3>Definitions</h3>

<p>
  <strong>Domestic PEP</strong> means a PEP who is or has been entrusted with
  prominent public functions in the Anjouan and any family members or close
  associates of the PEP, regardless of the location of that PEP, those family
  members or close associates.
</p>

<p>
  <strong>Foreign PEP</strong> means a PEP who is or has been entrusted with
  prominent public functions outside of Anjouan and any family members or close
  associates of the PEP, regardless of the location of that PEP, those family
  members or close associates.
</p>

<p>
  A <strong>politically exposed person</strong> means a natural person who is or
  who has been entrusted with prominent public functions and includes the
  following:
</p>

<ul>
  <li>
    heads of State, heads of government, ministers and deputy or assistant
    ministers;
  </li>

  <li>members of parliament or of similar legislative bodies;</li>

  <li>members of the governing bodies of political parties;</li>

  <li>
    members of supreme courts, of constitutional courts or of other high-level
    judicial bodies, the decisions of which are not subject to further appeal,
    except in exceptional circumstances;
  </li>

  <li>members of courts of auditors or of the boards of central banks;</li>

  <li>
    ambassadors, chargés d'affaires and high-ranking officers in the armed
    forces;
  </li>

  <li>
    members of the administrative, management or supervisory bodies of
    State-owned enterprises; or
  </li>

  <li>
    directors, deputy directors and members of the board or equivalent function
    of an international organisation.
  </li>
</ul>

<p>
  No public function referred to in the above shall be understood as covering
  middle-ranking or more junior officials.
</p>

<h4>Family members</h4>

<p>
  <strong>Family members</strong> includes the spouse, or a person considered to
  be equivalent to a spouse, of a politically exposed person; the children and
  their spouses, or persons considered to be equivalent to a spouse, of a
  politically exposed person; or, the parents of a politically exposed person.
</p>

<h4>Close associates</h4>

<p>
  <strong>Persons known to be close associates</strong> means natural persons
  who are known to have joint beneficial ownership of legal entities or legal
  arrangements, or any other close business relations, with a politically
  exposed person; natural persons who have sole beneficial ownership of a legal
  entity or legal arrangement which is known to have been set up for the benefit
  of the PEP.
</p>

<p>
  It is your obligation to inform us of a change to your status as a PEP or
  Non-PEP should it change at any time in the future.
</p>
`

export { LegalPage as default }
